/**
 * CallRecordingPlayer (CALL-REC-1) — plays a captured phone-call recording
 * INLINE, natively, with zero navigation (Seth: "hit play and it plays, not a
 * bunch of other movements; don't link out of the app"). The audio lives in our
 * private `call-recordings` bucket; we hand the native <audio> element a
 * short-lived signed URL so desktop AND the mobile PWA get the OS scrubber for
 * free. Recordings are captured by `voice-webhook` (Telnyx TeXML recording
 * callback) onto `call_event.recording_path`. [[project_calls_telnyx]]
 *
 * Renders NOTHING until a recording actually exists, so it's safe to mount on a
 * surface before any calls have recorded (invisible, zero footprint).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone, Loader2, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useCallIntel } from "./calls";

const BUCKET = "call-recordings";

interface CallRecordingRow {
  id: string;
  recording_path: string | null;
  recording_seconds: number | null;
  recording_status: string | null;
  direction: string | null;
  from_e164: string | null;
  state: string | null;
  // CALLS-AI-01:
  transcript: string | null;
  ai_summary: string | null;
  ai_status: string | null;
}

/** Read a call_event's recording fields by id (e.g. a dispatch's source_call_id). */
export function useCallRecording(callId: string | null | undefined) {
  return useQuery({
    queryKey: ["call_recording", callId],
    enabled: !!callId,
    staleTime: 60 * 1000,
    // CALLS-AI-02: the AI runs by itself when a recording lands — while it's
    // listening, poll so the notes appear the moment they're ready.
    refetchInterval: (query) =>
      (query.state.data as CallRecordingRow | null | undefined)?.ai_status === "pending" ? 5000 : false,
    queryFn: async (): Promise<CallRecordingRow | null> => {
      const { data, error } = await supabase
        .from("call_event")
        .select("id, recording_path, recording_seconds, recording_status, direction, from_e164, state, transcript, ai_summary, ai_status")
        .eq("id", callId!)
        .maybeSingle();
      if (error) throw error;
      return (data as CallRecordingRow) ?? null;
    },
  });
}

/** Short-lived signed URL for one recording object (1 hour). */
function useRecordingUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ["call_recording_url", path],
    enabled: !!path,
    staleTime: 45 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path!, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

function prettyDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds < 1) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * Inline call-recording player. Pass EITHER a `callId` (resolves the path from
 * call_event) OR a `path` + `seconds` directly. Renders null when there's no
 * recording yet.
 */
export function CallRecordingPlayer({
  callId,
  path,
  seconds,
  className,
}: {
  callId?: string | null;
  path?: string | null;
  seconds?: number | null;
  className?: string;
}) {
  const rec = useCallRecording(callId ?? null);
  const resolvedPath = path ?? rec.data?.recording_path ?? null;
  const resolvedSeconds = seconds ?? rec.data?.recording_seconds ?? null;
  const url = useRecordingUrl(resolvedPath);

  // Nothing to show until a recording exists (call still in flight, never
  // recorded, or capture failed) — stay invisible.
  if (!resolvedPath) return null;

  return (
    <div className={className}>
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        <Phone className="h-3.5 w-3.5" /> Call recording
        {resolvedSeconds ? <span className="font-normal normal-case tracking-normal text-slate-400">· {prettyDuration(resolvedSeconds)}</span> : null}
      </div>
      {url.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading audio…
        </div>
      ) : url.data ? (
        <audio controls preload="none" src={url.data} className="h-9 w-full max-w-md">
          Your browser can&rsquo;t play this audio.
        </audio>
      ) : (
        <div className="text-xs text-slate-400">Recording unavailable.</div>
      )}
      {/* CALLS-AI-01: the AI listens to this recording — summary + transcript
          land right here, and the extraction chips light up search + the 360. */}
      {callId && <CallAiNotes callId={callId} row={rec.data ?? null} />}
    </div>
  );
}

function CallAiNotes({ callId, row }: { callId: string; row: CallRecordingRow | null }) {
  const intel = useCallIntel();
  const [showTranscript, setShowTranscript] = useState(false);
  if (!row) return null;

  if (row.ai_summary || row.transcript) {
    return (
      <div className="mt-2 rounded-lg border border-violet-100 bg-violet-50/50 px-3 py-2">
        {row.ai_summary && (
          <div className="flex items-start gap-1.5 text-xs text-slate-700">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
            <span>{row.ai_summary}</span>
          </div>
        )}
        {row.transcript && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setShowTranscript((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:text-violet-800"
            >
              {showTranscript ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showTranscript ? "Hide the transcript" : "Read the transcript"}
            </button>
            {showTranscript && (
              <pre className="mt-1 max-h-56 overflow-y-auto whitespace-pre-wrap rounded bg-white p-2 text-[11px] leading-relaxed text-slate-600 ring-1 ring-violet-100">
                {row.transcript}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }

  // The AI runs BY ITSELF when a recording lands (CALLS-AI-02, DB trigger) —
  // while it's working, show a quiet "listening" line, no action needed.
  if (row.ai_status === "pending" || intel.isPending) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
        <Loader2 className="h-3 w-3 animate-spin text-violet-400" /> AI is listening to the call — notes appear here on their own.
      </div>
    );
  }

  // Manual button = the exception path only: a run that FAILED, or a call
  // recorded before the auto wiring existed (ai_status null).
  if (!row.recording_path) return null;
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => intel.mutate({ call_id: callId })}
        title="The AI listens to the recording, writes a transcript, and pulls out the caller's details"
        className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700 transition-colors hover:bg-violet-100"
      >
        <Sparkles className="h-3 w-3" />
        {row.ai_status === "failed" ? "AI couldn't read that one — try again" : "AI notes — run on this older call"}
      </button>
      {intel.isError && <span className="ml-2 text-[11px] text-rose-600">{String((intel.error as Error)?.message ?? "failed")}</span>}
    </div>
  );
}
